import { useEffect, useState } from "react";
import {
  deleteProject,
  getProjectDeleteImpact,
  type ProjectDeleteImpact,
  type ProjectSummary,
} from "../lib/api";

/**
 * Delete-project confirmation dialog.
 *
 * Replaces the dangerous one-click delete on the Dashboard's project list.
 * Loads /api/projects/:id/delete-impact on mount so the user sees:
 *
 *   - exactly what's about to be wiped (crawl count + project name)
 *   - whether any active schedules will silently re-create the project on
 *     their next tick (schedules are URL-keyed, not project-keyed — they
 *     survive project delete by design; see plan §21 / Option-A
 *     auto-resolve)
 *
 * Type-to-confirm gate: the Delete button is disabled until the user
 * types the project name verbatim. The same gate copy + behavior the
 * GitHub repo-delete dialog uses — battle-tested anti-fat-finger UX.
 *
 * Design choices kept narrow:
 *   - No "delete and recreate" affordance. If they want a clean reset,
 *     delete + re-crawl. Compound actions raise more questions than
 *     they answer.
 *   - No "also delete schedules" checkbox. The schedule warning is
 *     advisory; the user manages schedules from /schedules. Bundling
 *     two destructive actions into one click is the UX failure mode
 *     this whole dialog exists to prevent.
 */
export function DeleteProjectDialog(props: {
  project: ProjectSummary;
  onClose: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const { project, onClose, onDeleted } = props;
  const [impact, setImpact] = useState<ProjectDeleteImpact | null>(null);
  const [impactErr, setImpactErr] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  // Fetch impact on mount. Failure here is non-fatal — the user can
  // still delete; we just won't be able to warn them about schedules.
  useEffect(() => {
    let alive = true;
    getProjectDeleteImpact(project.id)
      .then((r) => {
        if (alive) setImpact(r);
      })
      .catch((err) => {
        if (alive) {
          setImpactErr(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      alive = false;
    };
  }, [project.id]);

  const canDelete = confirmText.trim() === project.name && !busy;

  async function handleDelete(): Promise<void> {
    setBusy(true);
    setSubmitErr(null);
    try {
      await deleteProject(project.id);
      onDeleted();
    } catch (err) {
      setSubmitErr(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-project-title"
    >
      <div className="w-full max-w-lg rounded-lg border border-rose-900/60 bg-slate-950 p-5 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-rose-900/30 text-rose-300 text-xl">
            !
          </div>
          <div className="flex-1">
            <h2
              id="delete-project-title"
              className="text-base font-semibold text-slate-100"
            >
              Delete project &ldquo;{project.name}&rdquo;?
            </h2>
            <p className="mt-1 text-sm text-slate-400">
              This action cannot be undone.
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-2 rounded-md border border-slate-800 bg-slate-900/40 p-3 text-sm text-slate-300">
          {impact === null && impactErr === null && (
            <p className="text-slate-500">Loading impact…</p>
          )}
          {impactErr !== null && (
            <p className="text-amber-400">
              Couldn&apos;t load impact summary: {impactErr}. You can still
              delete; you just won&apos;t see the affected counts.
            </p>
          )}
          {impact !== null && (
            <>
              <p>
                <span className="font-medium text-slate-100">
                  {impact.crawlCount}
                </span>{" "}
                crawl{impact.crawlCount === 1 ? "" : "s"} and every emitted
                test file under them will be permanently removed.
              </p>
              {impact.scheduleMatches > 0 && (
                <p className="rounded border border-amber-800/60 bg-amber-950/40 p-2 text-xs text-amber-200">
                  <span className="font-semibold">Heads up:</span>{" "}
                  {impact.scheduleMatches} active schedule
                  {impact.scheduleMatches === 1 ? "" : "s"} target{" "}
                  {impact.host ? (
                    <code className="font-mono text-amber-100">
                      {impact.host}
                    </code>
                  ) : (
                    "this host"
                  )}
                  . The next tick will silently recreate this project.{" "}
                  <a
                    href="/schedules"
                    className="underline hover:text-amber-100"
                  >
                    Pause those first
                  </a>{" "}
                  if you actually want this gone.
                </p>
              )}
              <p className="text-xs text-slate-500">
                Run history (in /runs) is preserved with a null project link
                — useful for audit trail. Anything else attached to this
                host (notifications, integrations) survives.
              </p>
            </>
          )}
        </div>

        <label className="mt-4 block text-xs uppercase tracking-wide text-slate-400">
          Type{" "}
          <code className="font-mono text-slate-200">{project.name}</code> to
          confirm
        </label>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          autoFocus
          disabled={busy}
          className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm font-mono text-slate-100 placeholder:text-slate-600 focus:border-rose-500 focus:outline-none"
          placeholder={project.name}
        />

        {submitErr !== null && (
          <p className="mt-2 text-sm text-rose-300">{submitErr}</p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleDelete()}
            disabled={!canDelete}
            className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-500 disabled:bg-rose-900 disabled:text-rose-400 disabled:cursor-not-allowed"
          >
            {busy ? "Deleting…" : "Delete project"}
          </button>
        </div>
      </div>
    </div>
  );
}
